/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { lazy, Suspense, useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import HeaderAndSlider from "../Components/HeaderAndSlider";
import { Vocabulary } from "./Vocabulary";
import Dashboard from "../Components/Dashboard/Dashboard";
import LoginPage from "../Components/LoginPage";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import withRole from "./withRole";
import type { Router } from "@remix-run/router";
import { componentNames } from "./Constants";
import { localUrlEnum } from "./urlEnum";
import VehicleOptionSlider from "../Components/VehicleDasboard/VehicleOptionSlider";

/**
 * LocalRouter component is responsible for rendering the appropriate routes based on the provided route paths.
 * It uses React Router to manage navigation and lazy loading for performance optimization.
 */

const Reports = lazy(() => import("../Components/Reports/Reports"));
const AdministrativeCostsDashboard = lazy(
  () => import("../Components/AdministrativeCosts/AdministrativeCostsDashboard")
);
const Logs = lazy(() => import("../Components/Logs/Logs"));
const UsersDashboard = lazy(() => import("../Components/Users/UsersDashboard"));
const MainSettings = lazy(() => import("../Components/Settings/MainSettings"));
const MainDashboard = lazy(() => import("../Components/MainDashboard"));
const VehicleDetails = lazy(() => import("../Components/VehicleDetails"));
const VehicleService = lazy(() => import("../Components/VehicleService"));
const VehicleDetailing = lazy(() => import("../Components/VehicleDetailing"));
const VehiclePainting = lazy(() => import("../Components/VehiclePainting"));
const VehicleAdditionalCost = lazy(
  () => import("../Components/VehicleAdditionalCost")
);
const VehiclePhotos = lazy(() => import("../Components/VehiclePhotos"));
const VehicleDocuments = lazy(() => import("../Components/VehicleDocuments"));
const VehicleGuarantees = lazy(() => import("../Components/VehicleGuarantees"));
const Clients = lazy(() => import("../Components/Clients/Clients"));
const NotFoundPage = lazy(() => import("./NotFoundPage"));
const DashboardFinancial = lazy(
  () => import("../Components/FinancialDepartment/DashboardFinancial")
);
const MainTemplates = lazy(() => import("../Components/Templates/MainTemplates"));
const DashboardClientMessages = lazy(
  () => import("../Components/MessageLists/DashboardClientMessages")
);
const LeadDetail = lazy(() => import("../Components/LeadManagement/LeadDetail"));
const DashboardSalesOnDemand = lazy(
  () => import("../Components/Sales/DashboardSalesOnDemand")
);
const DashboardFinancingMessages = lazy(
  () => import("../Components/MessageLists/DashboardFinancingMessages")
);
const DashboardVINRequests = lazy(
  () => import("../Components/MessageLists/DashboardVINRequests")
);
const AnafTable = lazy(() => import("../Components/ANAF/AnafTable"));
const UserSalaries = lazy(() => import("../Components/Salary/UserSalaries"));
const SecondVehiclesDashboard = lazy(
  () => import("../Components/VehicleDasboard/SecondVehiclesDashboard")
);
const DocumentsTable = lazy(() => import("../Components/Documents/DocumentsTable"));
const SalaryDashboard = lazy(() => import("../Components/Dashboard/SalaryDashboard"));
const XAutoDashboard = lazy(() => import("../Components/XAuto/XAutoDashboard"));
const Applications = lazy(() => import("../Components/Settings/Applications"));
const TasksBacklog = lazy(() => import("../Components/Tasks/TasksBacklog"));
const CalendarDashboard = lazy(() => import("../Components/Calendar/CalendarDashboard"));

type LocalRouterProps = {
  routes: Array<string> | undefined;
};

/**
 * Adds a vehicle option menu to the provided component.
 * @param component The component to which to add the vehicle option menu.
 * @param title The title for the vehicle option menu.
 * @returns The component with the vehicle option menu added.
 */
function addVehicleOptionsMenu(component: any, title: string) {
  return <VehicleOptionSlider title={title} component={component} />;
}

/**
 * Adds a header and menu to the provided component.
 * @param component The component to which to add the header and menu.
 * @param title The title for the header.
 * @returns The component with the header and menu added.
 */
function addHeaderAndMenu(component: any, title: string) {
  return <HeaderAndSlider key={title} title={title} component={component} />;
}

/**
 * Renders a fallback component while routes are loading.
 * @returns A React element representing the route loading fallback.
 */
function RouteLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress color="primary" aria-label={Vocabulary.loading} />
    </div>
  );
}

/**
 * Defines the authentication routes for the application.
 * @remarks
 * These routes are accessible without authentication and include the login page.
 */
const authRoutes = [
  {
    path: "/login",
    element: <LoginPage />,
  },
];

/**
 * Defines the dashboard routes for the application.
 * @remarks
 * These routes are accessible after authentication and include the main dashboard and home page.
 */
const dashboardRoutes = [
  {
    path: "/home",
    element: addHeaderAndMenu(<Dashboard />, Vocabulary.dashboard),
  },
  {
    path: "/",
    element: addHeaderAndMenu(
      <Dashboard name={componentNames.Dashboard} />,
      Vocabulary.dashboard
    ),
  },
];

/**
 * Defines the vehicle-related routes for the application.
 * @remarks
 * These routes are accessible after authentication and include various vehicle management pages.
 */
const vehicleRoutes = [
  {
    path: "/vehiclesDashboard",
    element: addHeaderAndMenu(
      <MainDashboard name="MainDashboard" />,
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDashboard/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(
        <SecondVehiclesDashboard name={componentNames.VehiclesDashboard} />,
        Vocabulary.vehicles
      ),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDetails/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(
        <VehicleDetails name={componentNames.VehicleMainInfo} />,
        Vocabulary.vehicles
      ),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleGuarantees/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleGuarantees />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleService/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleService />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDetailing/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleDetailing />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehiclePainting/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehiclePainting />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleAdditionalCost/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleAdditionalCost />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehiclePhotos/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehiclePhotos />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: "/vehiclesDashboard/vehicleDocuments/:id?",
    element: addHeaderAndMenu(
      addVehicleOptionsMenu(<VehicleDocuments />, Vocabulary.vehicles),
      Vocabulary.vehicles
    ),
  },
  {
    path: localUrlEnum.xautoVehiclesActive,
    element: addHeaderAndMenu(
      <XAutoDashboard />,
      Vocabulary.xautoVehiclesActive
    ),
  },
  {
    path: localUrlEnum.xautoVehiclesDeactivated,
    element: addHeaderAndMenu(
      <XAutoDashboard />,
      Vocabulary.xautoVehiclesDeactivated
    ),
  },
];

/**
 * Defines the sales and financial routes for the application.
 * @remarks
 * These routes are accessible after authentication and include various sales and financial management pages.
 */
const salesAndFinancialRoutes = [
  {
    path: "/financialDepartment",
    element: addHeaderAndMenu(
      <DashboardFinancial />,
      Vocabulary.dashboardFinancial
    ),
  },
  {
    path: "/onDemand",
    element: addHeaderAndMenu(
      <DashboardSalesOnDemand
        name={componentNames.DashboardClientMessages}
      />,
      Vocabulary.onDemand
    ),
  },
  {
    path: "/administrativeCosts",
    element: addHeaderAndMenu(
      <AdministrativeCostsDashboard />,
      Vocabulary.costsName
    ),
  },
];

/**
 * Defines the client and lead management routes for the application.
 * @remarks
 * These routes are accessible after authentication and include various client and lead management pages.
 */
const clientAndLeadRoutes = [
  {
    path: "/clients",
    element: addHeaderAndMenu(<Clients />, Vocabulary.clientsName),
  },
  {
    path: localUrlEnum.clientMessages,
    element: addHeaderAndMenu(
      <DashboardClientMessages
        name={componentNames.DashboardClientMessages}
      />,
      Vocabulary.clientMessages
    ),
  },
  {
    path: `${localUrlEnum.clientMessages}/:id`,
    element: addHeaderAndMenu(<LeadDetail />, Vocabulary.clientMessages),
  },
  {
    path: localUrlEnum.financingRequests,
    element: addHeaderAndMenu(
      <DashboardFinancingMessages
        name={componentNames.DashboardClientMessages}
      />,
      Vocabulary.financingRequests
    ),
  },
  {
    path: localUrlEnum.vinRequests,
    element: addHeaderAndMenu(
      <DashboardVINRequests name={componentNames.DashboardClientMessages} />,
      Vocabulary.vinRequests
    ),
  },
];

/**
 * Defines the administrative routes for the application.
 * @remarks
 * These routes are accessible after authentication and include various administrative management pages.
 */
const adminRoutes = [
  {
    path: "/applications",
    element: addHeaderAndMenu(<Applications />, Vocabulary.applications),
  },
  {
    path: "/usersDashboard",
    element: addHeaderAndMenu(<UsersDashboard />, Vocabulary.usersName),
  },
  {
    path: "/settings",
    element: addHeaderAndMenu(<MainSettings />, Vocabulary.settings),
  },
  {
    path: "/templates",
    element: addHeaderAndMenu(<MainTemplates />, Vocabulary.templatesName),
  },
  {
    path: "/logs",
    element: addHeaderAndMenu(<Logs />, Vocabulary.logsName),
  },
];

/**
 * Defines the salary management routes for the application.
 * @remarks
 * These routes are accessible after authentication and include various salary management pages.
 */
const salaryRoutes = [
  {
    path: "/salaries",
    element: addHeaderAndMenu(<UserSalaries />, Vocabulary.salaries),
  },
  {
    path: "/salaries/user/:id?/:month?/:year?",
    element: addHeaderAndMenu(
      <SalaryDashboard name={componentNames.SalaryComponent} />,
      Vocabulary.dashboard
    ),
  },
];

/**
 * Defines the operations-related routes for the application.
 * @remarks
 * These routes are accessible after authentication and include various operations management pages.
 */
const operationsRoutes = [
  {
    path: "/anaf/notifications",
    element: addHeaderAndMenu(<AnafTable />, Vocabulary.anafTable),
  },
  {
    path: "/documents",
    element: addHeaderAndMenu(<DocumentsTable />, Vocabulary.documentsTable),
  },
  {
    path: "/tasks-backlog",
    element: addHeaderAndMenu(
      <TasksBacklog name={componentNames.TasksBacklog} />,
      Vocabulary.tasksName
    ),
  },
  {
    path: "/calendar",
    element: addHeaderAndMenu(<CalendarDashboard />, Vocabulary.calendarName),
  },
  {
    path: "/reports",
    element: addHeaderAndMenu(<Reports />, Vocabulary.reportsName),
  },
];

/**
 * Defines the fallback route for the application, which is displayed when no other routes match.
 */
const notFoundRoute = {
  path: "*",
  element: addHeaderAndMenu(<NotFoundPage />, Vocabulary.paginaNegasita),
};

/**
 * Combines all defined routes into a single array for use in the LocalRouter component.
 */
const routerPaths = [
  ...authRoutes,
  ...dashboardRoutes,
  ...vehicleRoutes,
  ...salesAndFinancialRoutes,
  ...clientAndLeadRoutes,
  ...adminRoutes,
  ...salaryRoutes,
  ...operationsRoutes,
  notFoundRoute,
];

/**
 * A router component that dynamically creates routes based on the provided props.
 * @param props  The props for the LocalRouter component, including the routes to be rendered.
 * @returns  A React element representing the LocalRouter component.
 */
function LocalRouter(props: LocalRouterProps) {
  const [router, setRouter] = useState<Router>();

  useEffect(() => {
    if (!props.routes) return;

    const newRouterPaths = routerPaths.filter((crtRoute) => {
      return props.routes && props.routes.indexOf(crtRoute.path) >= 0;
    });
    setRouter(createBrowserRouter(newRouterPaths));
  }, [props]);

  return (
    router && (
      <Suspense fallback={<RouteLoadingFallback />}>
        <RouterProvider router={router} />
      </Suspense>
    )
  );
}

export default withRole(LocalRouter);
